import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, TextInput, NumberInput, Textarea, Switch } from '../../components/ui';
import { PlusIcon, TrashIcon } from '../../components/ui/Icons';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { useCatalogItems } from '../catalog-items/useData';
import { useOffers } from './useData';
import api from '../../services/api';

/** Fetches catalog item image with auth and displays as thumbnail (img src does not send JWT). */
export function CatalogItemThumbnail({
  catalogItemId,
  fileId,
  className,
  alt,
}: {
  catalogItemId: number;
  fileId: number;
  className?: string;
  alt?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let blobUrl: string | null = null;
    api
      .get(`/catalog-items/${catalogItemId}/files/${fileId}`, { responseType: 'blob' })
      .then((res) => {
        blobUrl = URL.createObjectURL(res.data as Blob);
        setSrc(blobUrl);
      })
      .catch(() => setError(true));
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
    };
  }, [catalogItemId, fileId]);
  if (error || !src) {
    return (
      <div
        className={cn(className, 'bg-gray-100 dark:bg-gray-800 flex items-center justify-center')}
        aria-hidden
      >
        <span className="text-xs text-gray-400">…</span>
      </div>
    );
  }
  return <img src={src} alt={alt ?? ''} className={className} />;
}

export interface OfferLineItemRow {
  name: string;
  description: string;
  model: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  amount: number;
  catalogItemId?: number | null;
}

interface OfferLineItemsEditorProps {
  items: OfferLineItemRow[];
  onChange: (items: OfferLineItemRow[]) => void;
  formatCurrency: (amount?: number | null) => string;
}

export const OfferLineItemsEditor: React.FC<OfferLineItemsEditorProps> = ({ items, onChange, formatCurrency }) => {
  const { colors } = useTheme();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFromOfferModalOpen, setImportFromOfferModalOpen] = useState(false);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<Set<number>>(new Set());
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [importingOffer, setImportingOffer] = useState(false);
  const [showDescription, setShowDescription] = useState(true);
  const { data: catalogItems, isLoading: loadingCatalog } = useCatalogItems(undefined, true);
  const { data: offersList, isLoading: loadingOffers } = useOffers();

  const updateRow = (index: number, updates: Partial<OfferLineItemRow>) => {
    const next = [...items];
    const row = { ...next[index], ...updates };
    if (updates.quantity !== undefined || updates.unitPrice !== undefined || updates.discount !== undefined) {
      const lineDiscount = row.discount ?? 0;
      row.amount = Math.max(0, row.quantity * row.unitPrice - lineDiscount);
    }
    next[index] = row;
    onChange(next);
  };

  const addRow = () => {
    onChange([...items, { name: '', description: '', model: '', quantity: 1, unitPrice: 0, discount: 0, amount: 0 }]);
  };

  const toggleCatalogSelection = useCallback((id: number) => {
    setSelectedCatalogIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const addFromCatalog = useCallback(() => {
    if (selectedCatalogIds.size === 0) return;
    const toAdd: OfferLineItemRow[] = catalogItems
      .filter((c) => selectedCatalogIds.has(c.id))
      .map((c) => {
        const unitPrice = c.defaultPrice ?? 0;
        const quantity = 1;
        return {
          name: c.name,
          description: c.description ?? '',
          model: c.type ?? '',
          quantity,
          unitPrice,
          discount: 0,
          amount: quantity * unitPrice,
          catalogItemId: c.id,
        };
      });
    onChange([...items, ...toAdd]);
    setSelectedCatalogIds(new Set());
    setImportModalOpen(false);
  }, [catalogItems, selectedCatalogIds, items, onChange]);

  const addFromOffer = useCallback(async () => {
    if (selectedOfferId == null) return;
    setImportingOffer(true);
    try {
      const res = await api.get(`/offers/${selectedOfferId}`);
      const offer = res.data as { OfferLineItem?: Array<{ name?: string | null; description?: string | null; model?: string | null; quantity: number; unitPrice: number; discount?: number | null; amount: number; catalogItemId?: number | null }> };
      const lines = offer?.OfferLineItem ?? [];
      const toAdd: OfferLineItemRow[] = lines.map((l) => {
        const q = l.quantity ?? 1;
        const p = l.unitPrice ?? 0;
        const d = l.discount ?? 0;
        return {
          name: l.name ?? '',
          description: l.description ?? '',
          model: l.model ?? '',
          quantity: q,
          unitPrice: p,
          discount: d,
          amount: l.amount ?? Math.max(0, q * p - d),
          catalogItemId: l.catalogItemId ?? null,
        };
      });
      onChange([...items, ...toAdd]);
      setSelectedOfferId(null);
      setImportFromOfferModalOpen(false);
    } finally {
      setImportingOffer(false);
    }
  }, [selectedOfferId, items, onChange]);

  const removeRow = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    onChange(next);
  };

  const total = items.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
            <Switch checked={showDescription} onChange={(e) => setShowDescription(e.target.checked)} size="sm" />
            Show description
          </label>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setImportModalOpen(true)}>
              Import from catalog
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setImportFromOfferModalOpen(true)}>
              Import from offer
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <PlusIcon className="w-4 h-4" />
              Add line
            </Button>
          </div>
      </div>

      <Modal isOpen={importModalOpen} onClose={() => { setImportModalOpen(false); setSelectedCatalogIds(new Set()); }} size="lg">
        <ModalHeader>
          <ModalTitle>Import from catalog</ModalTitle>
          <button type="button" onClick={() => setImportModalOpen(false)} className="p-1 rounded hover:bg-black/10" aria-label="Close">
            <span className="sr-only">Close</span>
          </button>
        </ModalHeader>
        <ModalContent>
          <p className={cn('text-sm mb-3', colors.textMuted)}>Select catalog items to add as line items. Name and default price will be filled in.</p>
          {loadingCatalog ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading catalog...</p>
          ) : catalogItems.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No active catalog items. Add products or services in Catalog first.</p>
          ) : (
            <div className={cn('max-h-64 overflow-y-auto border rounded-lg', colors.border)}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={cn('bg-gray-50 dark:bg-gray-800/50 sticky top-0')}>
                  <tr>
                    <th className="w-10 px-3 py-2" />
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Type</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">Default price</th>
                  </tr>
                </thead>
                <tbody className={cn('divide-y divide-gray-200 dark:divide-gray-700', colors.surface)}>
                  {catalogItems.map((c) => (
                    <tr
                      key={c.id}
                      className={cn(
                        'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                        selectedCatalogIds.has(c.id) && 'bg-indigo-50 dark:bg-indigo-900/20'
                      )}
                      onClick={() => toggleCatalogSelection(c.id)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedCatalogIds.has(c.id)}
                          onChange={() => toggleCatalogSelection(c.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm">{c.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{c.type ?? '-'}</td>
                      <td className="px-3 py-2 text-sm text-right">{formatCurrency(c.defaultPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalContent>
        <ModalFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setImportModalOpen(false)}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={addFromCatalog} disabled={selectedCatalogIds.size === 0}>
            Add selected ({selectedCatalogIds.size})
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={importFromOfferModalOpen} onClose={() => { setImportFromOfferModalOpen(false); setSelectedOfferId(null); }} size="lg">
        <ModalHeader>
          <ModalTitle>Import from offer</ModalTitle>
          <button type="button" onClick={() => { setImportFromOfferModalOpen(false); setSelectedOfferId(null); }} className="p-1 rounded hover:bg-black/10" aria-label="Close">
            <span className="sr-only">Close</span>
          </button>
        </ModalHeader>
        <ModalContent>
          <p className={cn('text-sm mb-3', colors.textMuted)}>Select an offer to copy its line items into this offer.</p>
          {loadingOffers ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading offers...</p>
          ) : !offersList?.length ? (
            <p className={cn('text-sm', colors.textMuted)}>No offers found.</p>
          ) : (
            <div className={cn('max-h-64 overflow-y-auto border rounded-lg', colors.border)}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={cn('bg-gray-50 dark:bg-gray-800/50 sticky top-0')}>
                  <tr>
                    <th className="w-10 px-3 py-2" />
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Offer</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Client</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Date</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">Lines</th>
                  </tr>
                </thead>
                <tbody className={cn('divide-y divide-gray-200 dark:divide-gray-700', colors.surface)}>
                  {offersList.map((o) => (
                    <tr
                      key={o.id}
                      className={cn(
                        'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                        selectedOfferId === o.id && 'bg-indigo-50 dark:bg-indigo-900/20'
                      )}
                      onClick={() => setSelectedOfferId(selectedOfferId === o.id ? null : o.id)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          checked={selectedOfferId === o.id}
                          onChange={() => setSelectedOfferId(selectedOfferId === o.id ? null : o.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-full border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm font-medium">{o.offerNumber ?? `#${o.id}`}</td>
                      <td className="px-3 py-2 text-sm">{o.client?.clientName ?? o.client?.clientName2 ?? '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                        {o.offerDate ? new Date(o.offerDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-right">{o.OfferLineItem?.length ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalContent>
        <ModalFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setImportFromOfferModalOpen(false)}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={addFromOffer} disabled={selectedOfferId == null || importingOffer}>
            {importingOffer ? 'Importing…' : 'Import line items'}
          </Button>
        </ModalFooter>
      </Modal>

      <div className={cn('w-full overflow-x-auto border rounded-lg', colors.border)}>
        <table className="w-full min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className={cn('bg-gray-50 dark:bg-gray-800/50')}>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 w-16">Image</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Product</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 w-16">Qty</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 w-20">Unit price</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 w-20">Value</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 w-16">Discount</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 w-20">Amount</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody className={cn(colors.surface)}>
            {items.map((row, index) => {
              const catalogItem = row.catalogItemId ? catalogItems.find((c) => c.id === row.catalogItemId) : null;
              const attachments = catalogItem?.FileAttachment ?? [];
              const primaryId = catalogItem?.primaryFileAttachmentId;
              const chosenFile = primaryId ? attachments.find((f) => f.id === primaryId) : attachments[0];
              const firstFile = chosenFile ?? attachments[0];
              const isImage = firstFile?.fileType?.toLowerCase().startsWith('image/');
              return (
                <React.Fragment key={index}>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-3 py-2 align-middle">
                      <div className="relative w-12 h-12 flex-shrink-0">
                        {firstFile && row.catalogItemId && isImage ? (
                          <CatalogItemThumbnail
                            catalogItemId={row.catalogItemId}
                            fileId={firstFile.id}
                            className="w-12 h-12 object-cover rounded border border-gray-200 dark:border-gray-600 absolute inset-0"
                            alt=""
                          />
                        ) : (
                          <div
                            className={cn(
                              'w-12 h-12 rounded border border-dashed flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-400 absolute inset-0'
                            )}
                            aria-hidden
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <TextInput
                        value={row.name}
                        onChange={(e) => updateRow(index, { name: e.target.value ?? '' })}
                        placeholder="Name"
                        size="sm"
                        showClearButton={false}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <NumberInput
                        value={row.quantity}
                        onChange={(e) => updateRow(index, { quantity: e.target.value ?? 0 })}
                        min={0}
                        step={1}
                        allowDecimals={false}
                        size="sm"
                        showClearButton={false}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <NumberInput
                        value={row.unitPrice}
                        onChange={(e) => updateRow(index, { unitPrice: e.target.value ?? 0 })}
                        min={0}
                        step={0.01}
                        size="sm"
                        showClearButton={false}
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-sm text-gray-600 dark:text-gray-400">{formatCurrency(row.quantity * row.unitPrice)}</td>
                    <td className="px-3 py-2 text-right">
                      <NumberInput
                        value={row.discount ?? 0}
                        onChange={(e) => updateRow(index, { discount: e.target.value ?? 0 })}
                        min={0}
                        step={0.01}
                        size="sm"
                        showClearButton={false}
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-medium">{formatCurrency(row.amount)}</td>
                    <td className="px-2 py-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRow(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Remove line"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                  {showDescription && (
                    <tr>
                      <td />
                      <td className="px-3 pt-0 pb-2">
                        <Textarea
                          value={row.description}
                          onChange={(e) => updateRow(index, { description: e.target.value ?? '' })}
                          placeholder="Description"
                          rows={2}
                          className="text-sm text-gray-500 dark:text-gray-400 resize-y"
                        />
                      </td>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {items.length > 0 && (
        <div className={cn('text-right text-sm font-semibold', colors.text)}>
          Total: {formatCurrency(total)}
        </div>
      )}
    </div>
  );
};
